/**
 * The in-app API reference, routed at `/api-reference` as `<page-api-reference>`.
 *
 * `SC-F202`. Story: `SC-S201`. It is the client half of a feature whose server
 * half already shipped: `internal/apidoc` assembles OpenAPI 3.0.3 from the
 * `init()` registrations every route package makes and serves it anonymously at
 * `GET /v1/openapi.json` (`06` §3.1), and a malformed operation fails the boot.
 * Nothing here generates, edits or supplements that document — this page renders
 * it and nothing else, which is the whole point: a reference that restated any
 * part of the surface would be a second source of truth that drifts.
 *
 * **The viewer is lazily imported.** `rapidoc` is a large bundle and this is one
 * page of sixty-four, so the custom element is pulled in on first render exactly
 * as `bn-code` pulls in CodeMirror. A reader who never opens the reference never
 * downloads it.
 *
 * **Try-It is disabled, deliberately.** `rapidoc` ships an interactive console
 * and it is turned OFF (`allow-try="false"`). Two reasons, and the second is the
 * one that decides it:
 *
 *   - The document is served anonymously, so the console would have no
 *     credential to send and every call would answer `401`. Injecting the
 *     reader's session token instead would make a documentation page into a
 *     client that can mint API keys, publish a generation and delete an
 *     instance, with the confirmation ceremonies, the typed-name confirmations
 *     and the step-up gates the product's own screens carry all bypassed.
 *   - `06` §1.7 requires a client-generated `Idempotency-Key` on the six
 *     expensive operations and `If-Match` on every mutable resource. A generic
 *     console sends neither, so its successes and failures would both be
 *     misleading.
 *
 * So the page says what to use instead — the `sluicecloud` CLI (`SC-F215`), or
 * curl — rather than offering a button that would either fail or do something
 * dangerous. That is `concept.md` principle 8 applied to a docs page.
 *
 * **Colours come from the tokens, at runtime.** `rapidoc` is configured through
 * attributes that take literal colour values, and this tree forbids a literal
 * colour anywhere in `ui/src`. The values are therefore READ off the resolved
 * `--bino-*` custom properties with `getComputedStyle` and handed over, and they
 * are re-read when the theme changes. The design system stays the single source
 * of every colour on the screen, including inside a third-party shadow root.
 *
 * **The four states.** *Loading* is the document fetch plus the viewer import;
 * *empty* is a document that parsed and declares no paths at all, which would
 * mean an `apidoc` registration regression rather than an empty product;
 * *error* renders the `apierr` code and the `request_id`; *populated* is the
 * viewer with the path and operation counts stated beside it, because a reader
 * needs to know whether they are looking at the whole surface.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";

import { type ApiError, parseError, supportReference } from "../api/errors.js";
import { fetchOpenAPI, type OpenAPIDocument, countOperations } from "../api/apidoc.js";
import { onThemeChange, resolvedTheme } from "../theme.js";

/**
 * The `--bino-*` custom properties this page hands to the viewer, and the
 * `rapidoc` attribute each one feeds. Keeping the mapping as data rather than as
 * a wall of assignments is what makes it reviewable: a reader can check that
 * every colour on the screen came from a token by reading one table.
 */
const THEME_ATTRS: ReadonlyArray<readonly [attr: string, token: string]> = [
  ["bg-color", "--bino-surface"],
  ["text-color", "--bino-text"],
  ["header-color", "--bino-surface-raised"],
  ["primary-color", "--bino-primary"],
  ["nav-bg-color", "--bino-surface-inset"],
  ["nav-text-color", "--bino-text-secondary"],
  ["nav-hover-bg-color", "--bino-surface-raised"],
  ["nav-hover-text-color", "--bino-text"],
  ["nav-accent-color", "--bino-primary"],
];

/** The rapidoc element's own type is not published, so it is addressed through
 * the minimal surface this page uses: one property for the parsed document and
 * `setAttribute` for the theme. */
interface RapiDocElement extends HTMLElement {
  loadSpec(spec: unknown): void;
}

/** Module-level cache of the viewer import: opening the page twice must not
 * fetch the bundle twice, and a failed import must not be retried in a loop. */
let viewerModule: Promise<unknown> | null = null;

function loadViewer(): Promise<unknown> {
  viewerModule ??= import("rapidoc");
  return viewerModule;
}

@customElement("page-api-reference")
export class PageAPIReference extends LitElement {
  @state() private loading = true;
  @state() private doc: OpenAPIDocument | null = null;
  @state() private loadError: ApiError | null = null;
  /** Set when the viewer bundle itself could not be fetched. It is a separate
   * state from [loadError] because it is not an API failure and carries no
   * `request_id` — and because the remedy differs: a reload, not a retry of a
   * call that already succeeded. */
  @state() private viewerFailed = false;

  /** Released on disconnect so a page that is navigated away from stops
   * re-theming an element that no longer exists. */
  private stopThemeWatch: (() => void) | null = null;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
    }
    header.intro {
      margin-bottom: var(--bino-space-md);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    p {
      margin: 0 0 var(--bino-space-sm);
      max-width: 62ch;
      font-size: var(--bino-font-size-md);
      line-height: 1.55;
      color: var(--bino-text-secondary);
    }
    .facts {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs) var(--bino-space-sm);
      align-items: center;
      margin-bottom: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text);
    }
    .ref {
      margin: 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      user-select: all;
    }
    /* The viewer is given a tall, scrollable frame of its own rather than being
       allowed to size the document: rapidoc renders its own left nav and expects
       to own a viewport-sized box. Wide operation tables scroll inside it, so the
       page body never scrolls sideways. */
    .frame {
      height: min(78vh, 60rem);
      overflow: hidden;
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
    }
    rapi-doc {
      display: block;
      width: 100%;
      height: 100%;
    }
    .actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    /* The two document links are anchors rather than buttons on purpose: they
       leave the SPA to a raw document, and a real href is what lets a reader
       middle-click, copy or curl it. */
    a.link {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-primary);
      text-decoration: none;
    }
    a.link:hover,
    a.link:focus-visible {
      text-decoration: underline;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
    // The viewer's colours are attributes, not CSS, so a theme flip has to be
    // pushed into it. Without this the reference stays light inside a dark app.
    this.stopThemeWatch = onThemeChange(() => this.applyTheme());
  }

  override disconnectedCallback() {
    this.stopThemeWatch?.();
    this.stopThemeWatch = null;
    super.disconnectedCallback();
  }

  private async load() {
    this.loading = true;
    this.loadError = null;
    this.viewerFailed = false;
    try {
      // The document first and the viewer second, so a served-but-broken
      // document is reported as an API failure rather than as a missing bundle.
      const doc = await fetchOpenAPI();
      await loadViewer().catch(() => {
        this.viewerFailed = true;
      });
      this.doc = doc;
    } catch (err) {
      this.loadError = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  override updated() {
    // rapidoc takes its document through a method rather than an attribute, so
    // the hand-off happens after the element exists. Re-handing the same object
    // is cheap and is what makes a theme re-render safe.
    const viewer = this.renderRoot.querySelector<RapiDocElement>("rapi-doc");
    if (viewer && this.doc && typeof viewer.loadSpec === "function") {
      viewer.loadSpec(this.doc);
    }
    this.applyTheme();
  }

  /**
   * applyTheme copies the resolved design-system colours into the viewer.
   *
   * The values are read from the HOST element, so they resolve through whatever
   * `data-theme` and `prefers-color-scheme` currently say — the same cascade
   * every other surface in the product reads. Nothing here is a literal.
   */
  private applyTheme() {
    const viewer = this.renderRoot.querySelector<RapiDocElement>("rapi-doc");
    if (!viewer) return;
    const styles = getComputedStyle(this);
    viewer.setAttribute("theme", resolvedTheme());
    for (const [attr, token] of THEME_ATTRS) {
      const value = styles.getPropertyValue(token).trim();
      if (value !== "") viewer.setAttribute(attr, value);
    }
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Loading the API reference"></bn-spinner>`;
    }
    if (this.loadError) return this.renderError(this.loadError);
    if (!this.doc) return this.renderError(parseError(new Error("no document")));
    const paths = Object.keys(this.doc.paths ?? {}).length;
    if (paths === 0) return this.renderEmpty();
    return this.renderDoc(this.doc, paths);
  }

  /**
   * The error state. It carries the `apierr` code and the `request_id`, because
   * `SC-S208` makes both part of every failure a reader might quote, and because
   * this page's failure is one of the few a support conversation starts from —
   * the document is served anonymously, so a `401` here means something is wrong
   * with the deployment rather than with the reader's session.
   */
  private renderError(err: ApiError) {
    const ref = supportReference(err);
    return html`<bn-card>
      <h1>The API reference could not be loaded</h1>
      <p>${err.message} <span class="code">${err.code}</span></p>
      <p>
        The document itself is served anonymously at
        <span class="code">/v1/openapi.json</span>, so this failure is about reaching Sluice Cloud
        rather than about your session.
      </p>
      ${ref ? html`<p class="ref">${ref}</p>` : nothing}
      <div class="actions">
        <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
        <a class="link" href="/v1/openapi.json" target="_blank" rel="noreferrer">
          Open the raw document
        </a>
      </div>
    </bn-card>`;
  }

  /**
   * The empty state, which should be unreachable and is built anyway. A document
   * that parses and declares no paths means `internal/apidoc` collected no
   * registrations — a regression in the `init()` wiring, not a product with no
   * API — so the copy says that rather than "there is nothing here".
   */
  private renderEmpty() {
    return html`<bn-card>
      <bn-empty-state
        icon="∅"
        heading="This deployment's API document declares no paths"
        message="The document was served and parsed, and it contains no operations. That points at the
        control plane's own route registration rather than at anything you can fix here — quote this
        screen when you report it."
      >
        <a slot="actions" class="link" href="/v1/openapi.json" target="_blank" rel="noreferrer">
          Open the raw document
        </a>
      </bn-empty-state>
    </bn-card>`;
  }

  private renderDoc(doc: OpenAPIDocument, paths: number) {
    const operations = countOperations(doc);
    return html`
      <header class="intro">
        <h1>API reference</h1>
        <p>
          Every screen in this product is built on the routes below — there is no private API. This
          document is generated from the control plane's own handler registrations, so it describes
          the deployment you are signed in to rather than a published snapshot.
        </p>
        <div class="facts">
          <bn-badge variant="neutral">${paths} paths</bn-badge>
          <bn-badge variant="neutral">${operations} operations</bn-badge>
          ${doc.info?.version ? html`<span>version ${doc.info.version}</span>` : nothing}
          <span class="code">/v1/openapi.json</span>
        </div>
        <p>
          <strong>Requests cannot be sent from this page.</strong> The reference is a reader, not a
          client: sending a real call would need your session's authority, and the expensive
          operations additionally require an <span class="code">Idempotency-Key</span> and an
          <span class="code">If-Match</span> that a generic console does not produce. Use the
          <span class="code">sluicecloud</span> CLI, or curl with an API key.
        </p>
        ${this.viewerFailed
          ? html`<p>
              The document loaded but the viewer did not. The raw document is linked below and is the
              same bytes this page would have rendered.
            </p>`
          : nothing}
      </header>

      ${this.viewerFailed
        ? nothing
        : html`<div class="frame">
            <rapi-doc
              render-style="read"
              show-header="false"
              show-info="false"
              allow-try="false"
              allow-authentication="false"
              allow-server-selection="false"
              allow-spec-url-load="false"
              allow-spec-file-load="false"
              schema-style="table"
              default-schema-tab="schema"
              sort-endpoints-by="path"
              regular-font="var(--bino-font-sans)"
              mono-font="var(--bino-font-mono)"
            ></rapi-doc>
          </div>`}

      <div class="actions">
        <a class="link" href="/v1/openapi.json" target="_blank" rel="noreferrer">Download JSON</a>
        <a class="link" href="/v1/openapi.yaml" target="_blank" rel="noreferrer">Download YAML</a>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-api-reference": PageAPIReference;
  }
}
